<template>
  <highcharts :options="donutChartOption" />
</template>
  
<script setup lang="ts">
import { useBreakpoints, useVModel } from '@vueuse/core'
import type { ColorType, TooltipFormatterContextObject } from 'highcharts'
//   import moment from 'moment/moment'
import { computed } from 'vue'

import type { LivewatchScheduleSupplyCategoryPercent } from '~/api/service/livewatch/schedule-supply-donut.service'
import { setCommaWithDigits } from '~/utils/numberFormat'
  
  interface Props {
    modelValue: number
    series: Array<LivewatchScheduleSupplyCategoryPercent>
    colorMap?: Record<string, ColorType>
  }
  
const props = defineProps<Props>()
const selectedDate = useVModel(props, 'modelValue')
const { createCustomTooltip } = useChartTooltip()

// Detect mobile viewport (<= 768px)
const breakpoints = useBreakpoints({ mobile: 768 })
const isMobile = breakpoints.smaller('mobile')

const donutChartOption = computed(() => {
  return {
    title: undefined,
    subtitle: {
      useHTML: true,
      text: new Date(selectedDate.value).toISOString().split('T')[0],
      style: {
        fontWeight: 'bold',
        color: 'white',
        fontSize: '16px',
      },
      floating: true,
      verticalAlign: 'middle',
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    chart: {
      type: 'pie',
      width: null,
      style: {
        color: 'white',
        fontSize: '12px',
        fontFamily: 'Montserrat',
      },
      backgroundColor: 'transparent',
      zooming: {
        mouseWheel: {
          enabled: false
        }
      },
      panning: {
        enabled: false
      }
    },
    xAxis: {
      labels: {
        style: {
          color: 'white',
          fontSize: '12px',
          fontFamily: 'Montserrat',
        },
      },
    },
    plotOptions: {
      pie: {
        cursor: 'pointer',
        innerSize: '50%',
        size: '70%',
        showInLegend: isMobile.value,
        enableMouseTracking: true,
        allowPointSelect: false,
        dataLabels: {
          enabled: !isMobile.value,
          useHTML: true,
          style: {
            textOutline: undefined,
            textOverflow: 'ellipsis',
          },
          formatter: function (this: any) {
            const name = this.key
            const percent = Number(this.point.options.percent).toFixed(2)
            return `
                <div class="flex flex-col gap-1">
                    <span class="font-bold text-[12px] text-white/40">${name}</span>
                    <span class="font-bold text-[12px] text-white">${percent}%</span>
                </div>
              `
          }
        },
      },
      series: {
        showInNavigator: false,
        borderWidth: 0,
        marker: {
          enabled: false,
        },
        dataGrouping: {
          enabled: false,
        },
      },
    },
    legend: {
      enabled: isMobile.value,
      floating: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      y: 0,
      useHTML: true,
      itemStyle: {
        color: '#FFFFFF',
      },
      labelFormatter: function (this: any) {
        return `<span style="color:#fff;">${this.name}</span>`
      },
    },
    series: [
      {
        type: 'pie',
        data: props.series.map((d: any) => ({
          ...d,
          color: props.colorMap?.[d.name] ?? d.color,
        })),
      },
    ],
    tooltip: {
      split: false,
      shared: true,
      shadow: false,
      outside: true,
      useHTML: true,
      padding: 8,
      backgroundColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      style: {
        fontSize: '12px',
        fontFamily: 'Montserrat',
      },
      formatter: function (this: TooltipFormatterContextObject) {
        return createCustomTooltip(this, {
          containerClass: 'w-[300px]',
          content: (point: any) => {
            return `<div class="flex flex-col w-full justify-between gap-2">
                    <div class="flex items-center gap-1">
                      <span style="color:${point.series.userOptions.color || point.color};">● </span>
                      <span class="font-bold">${point.options.name}</span>
                    </div>
                    <div class="flex justify-between items-center">
                      <span>Scheduled Supply</span>
                      <span class="font-bold">${setCommaWithDigits(point.options.y, 2)} NXPC</span>
                    </div>
                    <div class="flex justify-between items-center">
                      <span>Ratio</span>
                      <span class="font-bold">${Number(point.options.percent).toFixed(2)}%</span>
                    </div>
                </div>`
          },
        })
      }
    },
  }
})
</script>
  
<style scoped lang="scss">
  .chart-wrapper {
    width: calc(50% - 8px);
    position: relative;
    // @include flexCol(flex-start, center);
  
    // @include xs_mn {
    //   width: 100%;
    // }
  
    .selected-date {
      width: max-content;
      padding: 4px 8px;
      box-sizing: border-box;
      border-radius: 24px;
      background-color: rgba(var(--bc-dark), var(--o005));
      backdrop-filter: blur(32px);
  
      color: rgba(var(--bc-dark), var(--o1));
      font-size: 12px;
      font-weight: 700;
  
      position: absolute;
      top: 0;
      z-index: 10;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
    }
  
    .chart {
      width: 100%;
    }
  }
</style>